import React, { useEffect, useState } from "react";
import DashboardHeader from "./dashboard_header";
import SideMenu from "./dashboard_menu";
import Canvas from "./dahboard_canvas";
import DashboardFooter from "./dashboard_footer";
import EHRRecordViewer from "./components/EHRRecordViewer";
import EHRNav from "./components/EHRNav";
import localforage from "localforage";
import Swal from "sweetalert2";
import axios from "axios";
import { useBaseUrl } from "../../utils/utils";
import { Link } from "react-router-dom";

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

interface MedicalRecord {
    id: string;
    record_identifier: string;
    name: string;
    patient: {
        patient_id: string;
        first_name: string;
        last_name: string;
        email: string;
        gender: string;
        date_of_birth: string;
    };
    examined_by: {
        fname: string;
        lname: string;
        title: { name: string };
    };
    status: string;
    privacy_level: string;
    date_created: string;
    date_of_service: string;
    chief_complaint: string;
    diagnosis_description?: string;
    version: number;
    is_confidential: boolean;
    requires_follow_up: boolean;
    provider_signature: boolean;
    updated_at: string;
}

interface RecordStats { total: number; active: number; completed: number; draft: number; requires_follow_up: number; }

const AllRecords: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [records, setRecords] = useState<MedicalRecord[]>([]);
    const [filteredRecords, setFilteredRecords] = useState<MedicalRecord[]>([]);
    const [selectedRecord, setSelectedRecord] = useState<MedicalRecord | null>(null);
    const [showRecordModal, setShowRecordModal] = useState(false);
    const [recordStats, setRecordStats] = useState<RecordStats>({ total: 0, active: 0, completed: 0, draft: 0, requires_follow_up: 0 });

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [privacyFilter, setPrivacyFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState("all");
    const [sortBy, setSortBy] = useState("date_created");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

    const [viewMode, setViewMode] = useState<"cards" | "list">("cards");
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 12;

    const base_url = useBaseUrl();

    useEffect(() => { document.title = "Browse Records - Uzima EHR"; loadRecords(); }, []);
    useEffect(() => { applyFiltersAndSort(); }, [records, searchTerm, statusFilter, privacyFilter, dateFilter, sortBy, sortOrder]);

    const loadRecords = async () => {
        setIsLoading(true);
        try {
            const clinic_id = await localforage.getItem("clinic_id") as number;
            if (!clinic_id) { await Swal.fire({ icon: "error", title: "Authentication Error", text: "Unable to identify your clinic. Please log in again." }); return; }
            const url = `${base_url}/api/v1/medical-records/?clinic_id=${clinic_id}`;
            const response = await axios.get(url, { headers: { 'Content-Type': 'application/json' } });
            const { status, data } = response.data;
            if (status === "success" && data) { setRecords(data); calculateStats(data); }
        } catch (error: any) {
            console.error("Error loading records:", error);
            await Swal.fire({ icon: "error", title: "Connection Error", text: error.message || "Failed to load records." });
        } finally { setIsLoading(false); }
    };

    const calculateStats = (recordsData: MedicalRecord[]) => {
        const stats: RecordStats = {
            total: recordsData.length,
            active: recordsData.filter(r => r.status === 'active').length,
            completed: recordsData.filter(r => r.status === 'completed').length,
            draft: recordsData.filter(r => r.status === 'draft').length,
            requires_follow_up: recordsData.filter(r => r.requires_follow_up).length,
        };
        setRecordStats(stats);
    };

    const applyFiltersAndSort = () => {
        let filtered = [...records];

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(record => 
                record.name.toLowerCase().includes(term) ||
                record.patient.first_name.toLowerCase().includes(term) ||
                record.patient.last_name.toLowerCase().includes(term) ||
                record.patient.patient_id.toLowerCase().includes(term) ||
                record.record_identifier.toLowerCase().includes(term) ||
                record.chief_complaint.toLowerCase().includes(term) ||
                (record.diagnosis_description && record.diagnosis_description.toLowerCase().includes(term))
            );
        }

        // Apply status filter
        if (statusFilter !== "all") {
            filtered = filtered.filter(record => record.status === statusFilter);
        }

        // Apply privacy filter
        if (privacyFilter !== "all") {
            filtered = filtered.filter(record => record.privacy_level === privacyFilter);
        }

        // Apply date filter
        if (dateFilter !== "all") {
            const now = new Date();
            const filterDate = new Date();
            
            switch (dateFilter) {
                case "today":
                    filterDate.setHours(0, 0, 0, 0);
                    filtered = filtered.filter(record => 
                        new Date(record.date_created) >= filterDate
                    );
                    break;
                case "week":
                    filterDate.setDate(now.getDate() - 7);
                    filtered = filtered.filter(record => 
                        new Date(record.date_created) >= filterDate
                    );
                    break;
                case "month":
                    filterDate.setDate(now.getDate() - 30);
                    filtered = filtered.filter(record => 
                        new Date(record.date_created) >= filterDate
                    );
                    break;
            }
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue, bValue;
            
            switch (sortBy) {
                case "date_created":
                    aValue = new Date(a.date_created).getTime();
                    bValue = new Date(b.date_created).getTime();
                    break;
                case "date_of_service":
                    aValue = new Date(a.date_of_service).getTime();
                    bValue = new Date(b.date_of_service).getTime();
                    break;
                case "patient_name":
                    aValue = `${a.patient.first_name} ${a.patient.last_name}`.toLowerCase();
                    bValue = `${b.patient.first_name} ${b.patient.last_name}`.toLowerCase();
                    break;
                case "record_name":
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case "status":
                    aValue = a.status;
                    bValue = b.status;
                    break;
                default:
                    aValue = new Date(a.date_created).getTime();
                    bValue = new Date(b.date_created).getTime();
            }

            if (typeof aValue === "string" && typeof bValue === "string") {
                return sortOrder === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            } else {
                return sortOrder === "asc" ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
            }
        });

        setFilteredRecords(filtered);
        setCurrentPage(1); // Reset to first page when filters change
    };

    const STATUS_MAP: Record<string, { bg: string; text: string; label: string }> = {
        active: { bg: ACCENT_SOFT, text: ACCENT, label: 'Active' },
        completed: { bg: 'rgba(59,130,246,0.1)', text: '#2563eb', label: 'Completed' },
        draft: { bg: 'rgba(245,158,11,0.1)', text: '#d97706', label: 'Draft' },
        amended: { bg: 'rgba(139,92,246,0.1)', text: '#7c3aed', label: 'Amended' },
        'entered-in-error': { bg: 'rgba(244,63,94,0.1)', text: '#e11d48', label: 'Error' },
        cancelled: { bg: 'rgba(100,116,139,0.1)', text: '#64748b', label: 'Cancelled' },
    };

    const PRIVACY_MAP: Record<string, { bg: string; text: string; icon: string }> = {
        public: { bg: 'rgba(16,185,129,0.1)', text: '#059669', icon: 'bx bx-globe' },
        restricted: { bg: 'rgba(245,158,11,0.1)', text: '#d97706', icon: 'bx bx-lock-open' },
        confidential: { bg: 'rgba(244,63,94,0.1)', text: '#e11d48', icon: 'bx bx-lock' },
        highly_confidential: { bg: 'rgba(15,23,42,0.08)', text: '#1e293b', icon: 'bx bx-shield' },
    };

    const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

    const indexOfLast = currentPage * recordsPerPage;
    const indexOfFirst = indexOfLast - recordsPerPage;
    const currentRecords = filteredRecords.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

    const handleRecordClick = (r: MedicalRecord) => { setSelectedRecord(r); setShowRecordModal(true); };
    const closeModal = () => { setShowRecordModal(false); setSelectedRecord(null); };
    const handleRecordUpdated = () => loadRecords();
    const clearFilters = () => { setSearchTerm(""); setStatusFilter("all"); setPrivacyFilter("all"); setDateFilter("all"); setSortBy("date_created"); setSortOrder("desc"); };

    const renderBadge = (status: string, map: Record<string, any>) => {
        const c = map[status] || { bg: 'rgba(100,116,139,0.1)', text: '#64748b', label: status };
        return (
            <span style={{ padding: '3px 10px', borderRadius: 20, background: c.bg, color: c.text, fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' as const }}>
                {c.icon && <i className={c.icon} style={{ marginRight: 4 }}></i>}
                {c.label || status.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
            </span>
        );
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-folder-open me-2" style={{ color: ACCENT }}></i>Browse Records</h1>
                                <p>View, filter, and manage all electronic health records</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="sc-btn sc-btn-ghost" onClick={loadRecords}><i className="bx bx-refresh"></i> Refresh</button>
                                <Link to="/institution/ehr/new" className="sc-btn sc-btn-primary" style={{ textDecoration: 'none' }}><i className="bx bx-plus"></i> New Record</Link>
                            </div>
                        </div>

                        {/* EHR Navigation */}
                        <EHRNav />

                        {/* Stats */}
                        <div className="sc-stats" style={{ marginBottom: 20 }}>
                            {[
                                { label: 'Total Records', val: recordStats.total, icon: 'bx bx-file', cls: 'teal' },
                                { label: 'Active', val: recordStats.active, icon: 'bx bx-check-circle', cls: 'blue' },
                                { label: 'Completed', val: recordStats.completed, icon: 'bx bx-check-double', cls: 'violet' },
                                { label: 'Draft', val: recordStats.draft, icon: 'bx bx-edit', cls: 'amber' },
                                { label: 'Follow-up', val: recordStats.requires_follow_up, icon: 'bx bx-bell', cls: 'rose' },
                            ].map((s, i) => (
                                <div className="sc-stat" key={i}>
                                    <div className={`sc-stat-icon ${s.cls}`}><i className={s.icon}></i></div>
                                    <div className="sc-stat-value">{s.val}</div>
                                    <div className="sc-stat-label">{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Filters */}
                        <div className="sc-card" style={{ marginBottom: 20 }}>
                            <div className="sc-card-body" style={{ padding: '16px 20px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, alignItems: 'end' }}>
                                    <div>
                                        <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--uzima-shell-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, display: 'block' }}>Search</label>
                                        <div className="sc-search" style={{ marginBottom: 0 }}>
                                            <i className="bx bx-search"></i>
                                            <input type="text" placeholder="Name, ID, complaint..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ fontSize: '0.82rem' }} />
                                        </div>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--uzima-shell-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, display: 'block' }}>Status</label>
                                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, fontSize: '0.82rem', background: '#fff', color: 'var(--uzima-shell-text)' }}>
                                            <option value="all">All Statuses</option>
                                            <option value="active">Active</option>
                                            <option value="completed">Completed</option>
                                            <option value="draft">Draft</option>
                                            <option value="amended">Amended</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--uzima-shell-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, display: 'block' }}>Privacy</label>
                                        <select value={privacyFilter} onChange={e => setPrivacyFilter(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, fontSize: '0.82rem', background: '#fff', color: 'var(--uzima-shell-text)' }}>
                                            <option value="all">All Levels</option>
                                            <option value="public">Public</option>
                                            <option value="restricted">Restricted</option>
                                            <option value="confidential">Confidential</option>
                                            <option value="highly_confidential">Highly Confidential</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--uzima-shell-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, display: 'block' }}>Date Range</label>
                                        <select value={dateFilter} onChange={e => setDateFilter(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, fontSize: '0.82rem', background: '#fff', color: 'var(--uzima-shell-text)' }}>
                                            <option value="all">All Time</option>
                                            <option value="today">Today</option>
                                            <option value="week">Past Week</option>
                                            <option value="month">Past Month</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--uzima-shell-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, display: 'block' }}>Sort By</label>
                                        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, fontSize: '0.82rem', background: '#fff', color: 'var(--uzima-shell-text)' }}>
                                            <option value="date_created">Date Created</option>
                                            <option value="date_of_service">Service Date</option>
                                            <option value="patient_name">Patient Name</option>
                                            <option value="status">Status</option>
                                        </select>
                                    </div>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")} title="Toggle sort order">
                                            <i className={`bx bx-sort-${sortOrder === "asc" ? "up" : "down"}`}></i>
                                        </button>
                                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={clearFilters} title="Clear filters"><i className="bx bx-x"></i></button>
                                    </div>
                                </div>

                                {/* Active filter chips */}
                                {(searchTerm || statusFilter !== "all" || privacyFilter !== "all" || dateFilter !== "all") && (
                                    <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--uzima-shell-text-muted)' }}>Active:</span>
                                        {searchTerm && <span style={{ padding: '2px 10px', borderRadius: 20, background: ACCENT_SOFT, color: ACCENT, fontSize: '0.72rem', fontWeight: 600 }}>Search: {searchTerm}</span>}
                                        {statusFilter !== "all" && <span style={{ padding: '2px 10px', borderRadius: 20, background: ACCENT_SOFT, color: ACCENT, fontSize: '0.72rem', fontWeight: 600 }}>Status: {statusFilter}</span>}
                                        {privacyFilter !== "all" && <span style={{ padding: '2px 10px', borderRadius: 20, background: ACCENT_SOFT, color: ACCENT, fontSize: '0.72rem', fontWeight: 600 }}>Privacy: {privacyFilter}</span>}
                                        {dateFilter !== "all" && <span style={{ padding: '2px 10px', borderRadius: 20, background: ACCENT_SOFT, color: ACCENT, fontSize: '0.72rem', fontWeight: 600 }}>Date: {dateFilter}</span>}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Toolbar */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--uzima-shell-text)' }}>
                                <i className="bx bx-file me-1" style={{ color: ACCENT }}></i>
                                Medical Records ({filteredRecords.length})
                                <span style={{ fontWeight: 400, color: 'var(--uzima-shell-text-muted)', marginLeft: 8 }}>
                                    Showing {indexOfFirst + 1}–{Math.min(indexOfLast, filteredRecords.length)}
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                                <button className={`sc-btn sc-btn-sm ${viewMode === 'cards' ? 'sc-btn-primary' : 'sc-btn-ghost'}`} onClick={() => setViewMode('cards')} title="Card view"><i className="bx bx-grid-alt"></i></button>
                                <button className={`sc-btn sc-btn-sm ${viewMode === 'list' ? 'sc-btn-primary' : 'sc-btn-ghost'}`} onClick={() => setViewMode('list')} title="List view"><i className="bx bx-list-ul"></i></button>
                            </div>
                        </div>

                        {/* Content */}
                        {isLoading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <div key={i} className="sc-card" style={{ height: 80, opacity: 0.5 }}>
                                        <div className="sc-card-body" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px' }}>
                                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e2e8f0' }}></div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ width: '40%', height: 14, borderRadius: 6, background: '#e2e8f0', marginBottom: 6 }}></div>
                                                <div style={{ width: '60%', height: 10, borderRadius: 6, background: '#e2e8f0' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredRecords.length === 0 ? (
                            <div className="sc-card">
                                <div className="sc-card-body" style={{ textAlign: 'center', padding: '60px 20px' }}>
                                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                        <i className="bx bx-search-alt" style={{ fontSize: '1.6rem', color: ACCENT }}></i>
                                    </div>
                                    <p style={{ fontWeight: 700, color: 'var(--uzima-shell-text)', fontSize: '1rem', marginBottom: 6 }}>No Records Found</p>
                                    <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)', marginBottom: 16 }}>
                                        {records.length === 0 ? "No medical records have been created yet." : "No records match your filters."}
                                    </p>
                                    {records.length === 0
                                        ? <Link to="/institution/ehr/new" className="sc-btn sc-btn-primary" style={{ textDecoration: 'none' }}><i className="bx bx-plus"></i> Create First Record</Link>
                                        : <button className="sc-btn sc-btn-ghost" onClick={clearFilters}><i className="bx bx-x"></i> Clear Filters</button>
                                    }
                                </div>
                            </div>
                        ) : viewMode === 'cards' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {currentRecords.map(r => (
                                    <div key={r.id} className="sc-card" style={{ cursor: 'pointer', transition: 'all 0.15s', borderLeft: r.is_confidential ? `4px solid #e11d48` : r.version > 1 ? `4px solid #d97706` : undefined }}
                                        onClick={() => handleRecordClick(r)}
                                        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = ''; }}
                                    >
                                        <div className="sc-card-body" style={{ padding: '14px 20px' }}>
                                            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                                                {/* Avatar */}
                                                <div style={{ width: 42, height: 42, borderRadius: '50%', background: `linear-gradient(135deg, ${ACCENT}, #14b8a6)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0 }}>
                                                    {r.patient.first_name.charAt(0)}{r.patient.last_name.charAt(0)}
                                                </div>
                                                {/* Info */}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--uzima-shell-text)' }}>{r.name}</div>
                                                            <div style={{ fontSize: '0.78rem', color: ACCENT, fontWeight: 600 }}>
                                                                {r.patient.first_name} {r.patient.last_name}
                                                                <span style={{ color: 'var(--uzima-shell-text-muted)', fontWeight: 400, marginLeft: 8 }}>ID: {r.patient.patient_id}</span>
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                            {renderBadge(r.status, STATUS_MAP)}
                                                            {renderBadge(r.privacy_level, PRIVACY_MAP)}
                                                            {r.version > 1 && <span style={{ padding: '3px 10px', borderRadius: 20, background: `linear-gradient(135deg, ${ACCENT}, #14b8a6)`, color: '#fff', fontSize: '0.68rem', fontWeight: 700 }}>v{r.version}</span>}
                                                        </div>
                                                    </div>
                                                    <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '4px 20px', fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)' }}>
                                                        <div><i className="bx bx-pulse me-1"></i><b>Complaint:</b> {r.chief_complaint}</div>
                                                        {r.diagnosis_description && <div><i className="bx bx-notepad me-1"></i><b>Diagnosis:</b> {r.diagnosis_description}</div>}
                                                        <div><i className="bx bx-user me-1"></i><b>By:</b> {r.examined_by?.title?.name || ''}. {r.examined_by?.fname} {r.examined_by?.lname}</div>
                                                        <div><i className="bx bx-calendar me-1"></i><b>Service:</b> {fmtDate(r.date_of_service)}</div>
                                                    </div>
                                                    {/* Flags */}
                                                    <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                                                        {r.provider_signature && <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#059669' }}><i className="bx bx-check-circle me-1"></i>Signed</span>}
                                                        {r.requires_follow_up && <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#d97706' }}><i className="bx bx-bell me-1"></i>Follow-up</span>}
                                                        {r.is_confidential && <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#e11d48' }}><i className="bx bx-lock me-1"></i>Confidential</span>}
                                                    </div>
                                                </div>
                                                {/* Action */}
                                                <button className="sc-btn sc-btn-primary sc-btn-sm" style={{ flexShrink: 0 }} onClick={e => { e.stopPropagation(); handleRecordClick(r); }}>
                                                    <i className="bx bx-show"></i> View
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            /* List/Table View */
                            <div className="sc-card" style={{ overflow: 'hidden' }}>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                        <thead>
                                            <tr style={{ background: 'rgba(15,23,42,0.025)' }}>
                                                {['Patient', 'Record', 'Complaint', 'Examined By', 'Date', 'Status', ''].map((h, i) => (
                                                    <th key={i} style={{ padding: '10px 14px', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', borderBottom: '1px solid var(--uzima-shell-border)' }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentRecords.map(r => (
                                                <tr key={r.id} style={{ cursor: 'pointer', transition: 'background 0.12s' }}
                                                    onClick={() => handleRecordClick(r)}
                                                    onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'rgba(15,118,110,0.03)'; }}
                                                    onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = ''; }}
                                                >
                                                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <div style={{ width: 30, height: 30, borderRadius: '50%', background: `linear-gradient(135deg, ${ACCENT}, #14b8a6)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.65rem', flexShrink: 0 }}>
                                                                {r.patient.first_name.charAt(0)}{r.patient.last_name.charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: 600 }}>{r.patient.first_name} {r.patient.last_name}</div>
                                                                <div style={{ fontSize: '0.7rem', color: 'var(--uzima-shell-text-muted)' }}>{r.patient.patient_id}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)', fontWeight: 600 }}>{r.name}</td>
                                                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.chief_complaint}</td>
                                                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)' }}>{r.examined_by?.title?.name || ''}. {r.examined_by?.fname} {r.examined_by?.lname}</td>
                                                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)', whiteSpace: 'nowrap' }}>{fmtDate(r.date_of_service)}</td>
                                                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)' }}>{renderBadge(r.status, STATUS_MAP)}</td>
                                                    <td style={{ padding: '10px 14px', borderBottom: '1px solid var(--uzima-shell-border)' }}>
                                                        <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={e => { e.stopPropagation(); handleRecordClick(r); }}><i className="bx bx-show"></i></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Pagination */}
                        {!isLoading && totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 20 }}>
                                <button className="sc-btn sc-btn-ghost sc-btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}><i className="bx bx-chevron-left"></i></button>
                                {Array.from({ length: totalPages }).map((_, i) => {
                                    const p = i + 1;
                                    if (p === 1 || p === totalPages || (p >= currentPage - 2 && p <= currentPage + 2)) {
                                        return <button key={p} className={`sc-btn sc-btn-sm ${currentPage === p ? 'sc-btn-primary' : 'sc-btn-ghost'}`} onClick={() => setCurrentPage(p)}>{p}</button>;
                                    }
                                    if (p === currentPage - 3 || p === currentPage + 3) return <span key={p} style={{ padding: '4px 8px', fontSize: '0.8rem', color: 'var(--uzima-shell-text-muted)' }}>…</span>;
                                    return null;
                                })}
                                <button className="sc-btn sc-btn-ghost sc-btn-sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}><i className="bx bx-chevron-right"></i></button>
                            </div>
                        )}

                    </div>
                </div>
            </div>

            <Canvas />
            <DashboardFooter />

            {selectedRecord && showRecordModal && (
                <EHRRecordViewer recordId={selectedRecord.id} isVisible={showRecordModal} onClose={closeModal} onRecordUpdated={handleRecordUpdated} />
            )}
        </>
    );
};

export default AllRecords;